import React, { useState, useEffect } from 'react';
import { TrendingUp, CheckCircle, Clock, AlertTriangle, Users, BookOpen, FileText, Download, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCurrentUser } from '@/data/users';

export default function CurriculumCompletionPage() {
  const currentUser = getCurrentUser();
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [selectedYear, setSelectedYear] = useState('all');
  const [completionData, setCompletionData] = useState({ teachers: [], yearLevels: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setCompletionData({
        teachers: [
          { id: 1, name: 'Joella Tamata', department: 'Mathematics', subjects_taught: 3, avg_completion: 85, lessons_delivered: 45, lessons_planned: 52, assessments_given: 8 },
          { id: 2, name: 'Johnny Still Tari', department: 'English', subjects_taught: 2, avg_completion: 92, lessons_delivered: 38, lessons_planned: 40, assessments_given: 6 },
          { id: 3, name: 'Rowen Tari', department: 'Basic Science', subjects_taught: 2, avg_completion: 78, lessons_delivered: 32, lessons_planned: 44, assessments_given: 5 },
          { id: 4, name: 'Nicholas Aga', department: 'French', subjects_taught: 1, avg_completion: 100, lessons_delivered: 42, lessons_planned: 42, assessments_given: 7 },
          { id: 5, name: 'Edward Banga', department: 'Agriculture', subjects_taught: 1, avg_completion: 65, lessons_delivered: 28, lessons_planned: 48, assessments_given: 4 },
          { id: 6, name: 'Kathleen Tahi', department: 'Social Science', subjects_taught: 2, avg_completion: 88, lessons_delivered: 35, lessons_planned: 40, assessments_given: 6 },
          { id: 7, name: 'Trevina Tari', department: 'Technology', subjects_taught: 2, avg_completion: 72, lessons_delivered: 30, lessons_planned: 42, assessments_given: 5 },
          { id: 8, name: 'Fr. Jacob Ute', department: 'Religious Education', subjects_taught: 1, avg_completion: 80, lessons_delivered: 34, lessons_planned: 42, assessments_given: 4 },
        ],
        yearLevels: [
          { year: 7, subjects: 8, avg_completion: 82, teachers: 6 },
          { year: 8, subjects: 8, avg_completion: 78, teachers: 6 },
          { year: 9, subjects: 10, avg_completion: 75, teachers: 8 },
          { year: 10, subjects: 10, avg_completion: 72, teachers: 8 },
        ]
      });
      setLoading(false);
    }, 500);
  }, []);

  const getCompletionBadge = (percentage) => {
    if (percentage >= 90) return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    if (percentage >= 75) return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
    if (percentage >= 60) return <Badge className="bg-yellow-100 text-yellow-800">Fair</Badge>;
    return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
  };

  const overallStats = {
    avgCompletion: Math.round(completionData.teachers.reduce((acc, t) => acc + t.avg_completion, 0) / (completionData.teachers.length || 1)),
    totalLessons: completionData.teachers.reduce((acc, t) => acc + t.lessons_delivered, 0),
    totalAssessments: completionData.teachers.reduce((acc, t) => acc + t.assessments_given, 0),
    excellentTeachers: completionData.teachers.filter(t => t.avg_completion >= 90).length,
    needsAttention: completionData.teachers.filter(t => t.avg_completion < 60).length
  };

  return (
    <div className="space-y-6" data-testid="curriculum-completion-page">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-green-500" />
            Curriculum Completion
          </h1>
          <p className="text-muted-foreground mt-1">Track curriculum delivery and teacher performance</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
            <SelectTrigger className="w-[130px]"><SelectValue placeholder="Term" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Term 1">Term 1</SelectItem>
              <SelectItem value="Term 2">Term 2</SelectItem>
              <SelectItem value="Term 3">Term 3</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline"><Download className="h-4 w-4 mr-2" />Export Report</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-green-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Avg Completion</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{overallStats.avgCompletion}%</div></CardContent></Card>
        <Card className="border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Lessons Delivered</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-blue-600">{overallStats.totalLessons}</div></CardContent></Card>
        <Card className="border-l-4 border-l-purple-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Assessments Given</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-purple-600">{overallStats.totalAssessments}</div></CardContent></Card>
        <Card className="border-l-4 border-l-emerald-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Top Performers</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-emerald-600">{overallStats.excellentTeachers}</div></CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Needs Support</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-red-600">{overallStats.needsAttention}</div></CardContent></Card>
      </div>

      <Tabs defaultValue="teachers" className="space-y-4">
        <TabsList><TabsTrigger value="teachers">By Teacher</TabsTrigger><TabsTrigger value="years">By Year Level</TabsTrigger></TabsList>

        <TabsContent value="teachers" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Teacher Performance</CardTitle><CardDescription>Curriculum completion rates by teacher for {selectedTerm}</CardDescription></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
              ) : (
                <div className="space-y-4">
                  {completionData.teachers.map((teacher) => (
                    <div key={teacher.id} className="bg-muted/30 rounded-lg p-4" data-testid={`teacher-${teacher.id}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold">{teacher.name}</h4>
                            {getCompletionBadge(teacher.avg_completion)}
                          </div>
                          <p className="text-sm text-muted-foreground">{teacher.department} - {teacher.subjects_taught} subject(s)</p>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center"><div className="text-2xl font-bold">{teacher.lessons_delivered}</div><div className="text-xs text-muted-foreground">Lessons</div></div>
                          <div className="text-center"><div className="text-2xl font-bold">{teacher.assessments_given}</div><div className="text-xs text-muted-foreground">Assessments</div></div>
                          <div className="w-32">
                            <div className="flex items-center justify-between mb-1"><span className="text-sm">Progress</span><span className="text-sm font-bold">{teacher.avg_completion}%</span></div>
                            <Progress value={teacher.avg_completion} className="h-2" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="years" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Year Level Summary</CardTitle><CardDescription>Curriculum completion by year level for {selectedTerm}</CardDescription></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {completionData.yearLevels.map((level) => (
                    <Card key={level.year} className="border-2" data-testid={`year-${level.year}`}>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                          <CardTitle>Year {level.year}</CardTitle>
                          {getCompletionBadge(level.avg_completion)}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div><div className="flex justify-between mb-1"><span className="text-sm">Completion</span><span className="text-sm font-bold">{level.avg_completion}%</span></div><Progress value={level.avg_completion} className="h-3" /></div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1"><BookOpen className="h-3 w-3 text-muted-foreground" />{level.subjects} Subjects</div>
                            <div className="flex items-center gap-1"><Users className="h-3 w-3 text-muted-foreground" />{level.teachers} Teachers</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" />Recommendations</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2"><CheckCircle className="h-4 w-4" />Top Performers</h4>
              <ul className="space-y-1 text-sm">
                {completionData.teachers.filter(t => t.avg_completion >= 90).map(t => (
                  <li key={t.id} className="flex justify-between"><span>{t.name}</span><span className="text-green-600 font-medium">{t.avg_completion}%</span></li>
                ))}
                {completionData.teachers.filter(t => t.avg_completion >= 90).length === 0 && <li className="text-muted-foreground">No teachers at 90%+ yet</li>}
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Needs Support</h4>
              <ul className="space-y-1 text-sm">
                {completionData.teachers.filter(t => t.avg_completion < 70).map(t => (
                  <li key={t.id} className="flex justify-between"><span>{t.name}</span><span className="text-amber-600 font-medium">{t.avg_completion}%</span></li>
                ))}
                {completionData.teachers.filter(t => t.avg_completion < 70).length === 0 && <li className="text-green-600">All teachers on track!</li>}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
